import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  Cliente,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

export enum ClienteQueryKeyEnum {
  CLIENTES = 'clientes',
  CLIENTE = 'cliente',
}

///* tanStack query
export const useFetchClientes = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetClientesParams> = {}) => {
  return useQuery({
    queryKey: [ClienteQueryKeyEnum.CLIENTES, ...Object.values(params ?? {})],
    queryFn: () => getClientes(params),
    enabled: enabled,
  });
};

export const useGetCliente = (id: string) => {
  return useQuery({
    queryKey: [ClienteQueryKeyEnum.CLIENTE, id],
    queryFn: () => getCliente(id),
    retry: false,
  });
};

export const useCreateCliente = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateClienteParams<T>) => createCliente(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ClienteQueryKeyEnum.CLIENTES],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Cliente creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateCliente = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  customSuccessCallback,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateClienteParams<T>) => updateCliente(params),
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: [ClienteQueryKeyEnum.CLIENTES],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Cliente actualizado correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteCliente = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: deleteCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ClienteQueryKeyEnum.CLIENTES],
      });
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetClientesParams = Partial<Cliente> & {
  page?: number;
  page_size?: number;
};

export type CreateClienteParamsForm = Omit<Cliente, 'id_cliente'>;

export type CreateClienteParams<T> = T;
export type UpdateClienteParams<T> = {
  id: string;
  data: T;
};

export const getClientes = async (params?: GetClientesParams) => {
  const stateParams = { ...params };
  const queryParams = getUrlParamsFetch(stateParams);

  return get<Cliente[]>(`${urlBackend}api/v1/cliente/?${queryParams}`, true);
};

export const getCliente = async (id: string) => {
  try {
    return await get<Cliente>(`${urlBackend}api/v1/cliente/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createCliente = async <T>(data: CreateClienteParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Cliente>(`${urlBackend}api/v1/cliente/`, data, true);
};

export const updateCliente = async <T>({
  id,
  data,
}: UpdateClienteParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Cliente>(`${urlBackend}api/v1/cliente/${id}/`, data, true);
};

export const deleteCliente = async (id: string) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return remove<Cliente>(`${urlBackend}api/v1/cliente/${id}/`, true);
};
